/**
 * Citation Highlight Scroll Positioning
 *
 * A citation is only useful if the reader lands on the sentence. Opening the
 * cited page is not enough — on a full-height A4 page rendered at 100% zoom the
 * bottom two thirds of the page sit below the fold, so a highlight anywhere past
 * the first third is invisible until the user hunts for it (F13/F14 report).
 *
 * These helpers move the viewer's scroll container so the highlight sits in the
 * upper third of the viewport — high enough to read the surrounding lines,
 * low enough that it does not look clipped.
 */

/** Where in the viewport the highlight should land (0 = top, 0.5 = centred) */
export const HIGHLIGHT_SCROLL_ANCHOR = 1 / 3

export interface HighlightScrollInput {
  /** Current scrollTop of the scroll container */
  containerScrollTop: number
  /** Visible height of the scroll container */
  containerHeight: number
  /** Total scrollable height of the container's content */
  containerScrollHeight: number
  /** Target's top edge relative to the container's visible top (may be negative) */
  targetTop: number
  /** Target's height */
  targetHeight: number
  /** Override the default anchor */
  anchor?: number
}

/**
 * Compute the scrollTop that puts the target at the anchor position.
 *
 * Returns the container's current scrollTop unchanged when the target is
 * already fully visible — re-anchoring a highlight the user can already see
 * would make the viewer jump for no reason.
 */
export function computeHighlightScrollTop({
  containerScrollTop,
  containerHeight,
  containerScrollHeight,
  targetTop,
  targetHeight,
  anchor = HIGHLIGHT_SCROLL_ANCHOR,
}: HighlightScrollInput): number {
  const maxScroll = Math.max(0, containerScrollHeight - containerHeight)

  const isFullyVisible = targetTop >= 0 && targetTop + targetHeight <= containerHeight
  if (isFullyVisible) return containerScrollTop

  const absoluteTop = containerScrollTop + targetTop

  // A highlight taller than the viewport can't be anchored — pin its top edge
  // so the reader starts at the beginning of the passage.
  const desired =
    targetHeight >= containerHeight ? absoluteTop : absoluteTop - containerHeight * anchor

  return Math.min(Math.max(desired, 0), maxScroll)
}

/**
 * Pick the visually highest element of a set.
 *
 * A passage highlight is several <mark>s (one per text-layer span) and a chunk
 * highlight is several rects; DOM order does not track visual order in a
 * pdf.js text layer, so scroll to whichever sits highest on the page.
 */
export function topmostElement(
  elements: Iterable<HTMLElement> | null | undefined
): HTMLElement | null {
  if (!elements) return null

  let best: HTMLElement | null = null
  let bestTop = Infinity

  for (const el of elements) {
    const top = el.getBoundingClientRect().top
    if (top < bestTop) {
      bestTop = top
      best = el
    }
  }

  return best
}

/**
 * Scroll `container` so `target` lands in the upper third of the viewport.
 *
 * Deliberately sets `scrollTop` rather than calling `Element.scrollIntoView`:
 * scrollIntoView walks every scrollable ancestor (the app shell, the dialog),
 * which in a docked panel drags unrelated chrome around, and it offers no way
 * to say "upper third".
 */
export function scrollHighlightIntoView(
  container: HTMLElement | null | undefined,
  target: HTMLElement | null | undefined,
  anchor: number = HIGHLIGHT_SCROLL_ANCHOR
): void {
  if (!container || !target) return

  const containerRect = container.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()

  container.scrollTop = computeHighlightScrollTop({
    containerScrollTop: container.scrollTop,
    containerHeight: containerRect.height || container.clientHeight,
    containerScrollHeight: container.scrollHeight,
    targetTop: targetRect.top - containerRect.top,
    targetHeight: targetRect.height,
    anchor,
  })
}
